import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Key, AlertCircle, Check, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginForm = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetMessage, setResetMessage] = useState(null);

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setShowPasswordReset(true);
      fetch(`${API_URL}/api/admin/password-reset/validate/${token}`)
        .then(res => res.json())
        .then(data => {
          if (!data.valid) {
            setResetMessage({ type: "error", text: data.message || "Token inválido" });
            setResetToken(null);
          }
        })
        .catch(() => {
          setResetMessage({ type: "error", text: "Error al validar el token" });
        });
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError("");

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem("admin_token", data.token);
        onLoginSuccess(data.user);
      } else {
        setAuthError(data.detail || "Credenciales incorrectas");
      }
    } catch (error) {
      setAuthError("Error de conexión al servidor");
    }
    setLoading(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetMessage(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetMessage({ type: "success", text: "Si el correo existe, recibirás un enlace para restablecer tu contraseña." });
        setResetEmail("");
      } else {
        setResetMessage({ type: "error", text: data.detail || "Error al procesar la solicitud" });
      }
    } catch (error) {
      setResetMessage({ type: "error", text: "Error de conexión. Intenta de nuevo." });
    }
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setResetMessage({ type: "error", text: "Las contraseñas no coinciden" });
      return;
    }
    
    if (newPassword.length < 8) {
      setResetMessage({ type: "error", text: "La contraseña debe tener al menos 8 caracteres" });
      return;
    }
    
    setLoading(true);
    setResetMessage(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetMessage({ type: "success", text: "¡Contraseña actualizada! Ya puedes iniciar sesión." });
        setResetToken(null);
        setNewPassword("");
        setConfirmPassword("");
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          setShowPasswordReset(false);
          setResetMessage(null);
        }, 3000);
      } else {
        setResetMessage({ type: "error", text: data.detail || "Error al restablecer la contraseña" });
      }
    } catch (error) {
      setResetMessage({ type: "error", text: "Error de conexión. Intenta de nuevo." });
    }
    setLoading(false);
  };

  const resetPasswordReset = () => {
    setShowPasswordReset(false);
    setResetMessage(null);
    setResetEmail("");
    setResetToken(null);
    setNewPassword("");
    setConfirmPassword("");
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Password Reset Form
  if (showPasswordReset) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {resetToken ? "Nueva Contraseña" : "Recuperar Contraseña"}
            </h1>
            <p className="text-gray-500 mt-2">
              {resetToken 
                ? "Ingresa tu nueva contraseña" 
                : "Ingresa tu correo electrónico para recibir un enlace"}
            </p>
          </div>

          {resetToken ? (
            <form onSubmit={handleConfirmReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Mínimo 8 caracteres"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Repite la contraseña"
                  required
                />
              </div>

              {resetMessage && (
                <div className={`flex items-center gap-2 text-sm ${
                  resetMessage.type === "success" ? "text-green-600" : "text-red-500"
                }`}>
                  {resetMessage.type === "success" ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {resetMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {loading ? "Actualizando..." : "Cambiar Contraseña"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="tu@email.com"
                  required
                />
              </div>

              {resetMessage && (
                <div className={`flex items-center gap-2 text-sm ${
                  resetMessage.type === "success" ? "text-green-600" : "text-red-500"
                }`}>
                  {resetMessage.type === "success" ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {resetMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {loading ? "Enviando..." : "Enviar Enlace"}
              </button>
            </form>
          )}

          <button
            onClick={resetPasswordReset}
            className="mt-6 w-full flex items-center justify-center gap-2 text-gray-600 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio de sesión
          </button>
        </motion.div>
      </div>
    );
  }

  // Normal Login Form
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
          <p className="text-gray-500 mt-2">Flowing Blog Manager</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Usuario</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="admin"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="••••••••"
              required
            />
          </div>

          {authError && (
            <div className="flex items-center gap-2 text-red-500 text-sm">
              <AlertCircle className="w-4 h-4" />
              {authError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            {loading ? "Verificando..." : "Iniciar Sesión"}
          </button>
        </form>

        <button
          onClick={() => setShowPasswordReset(true)}
          className="mt-4 w-full text-center text-sm text-gray-500 hover:text-orange-500 transition-colors"
        >
          ¿Olvidaste tu contraseña?
        </button>
      </motion.div>
    </div>
  );
};

export default LoginForm;
